import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format } from 'date-fns';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import CalendarGrid from 'garaje/components/calendar-grid';
import type DateLimits from 'garaje/types/date-time/date-limits';

/**
 * Single-month calendar component for single date selection.
 * For date range selection, use EnvoyTwoUpCalendar instead.
 *
 * Supports both controlled and uncontrolled modes:
 * - Uncontrolled (default): Component manages its own month navigation
 * - Controlled: Pass @currentMonth and handle @onGoBackOneMonth/@onGoToNextMonth
 *
 * Date selection: Defaults to today's date when no @selectedDate is provided
 */
interface EnvoyOneUpCalendarSignature {
  Args: {
    currentMonth?: Date;
    dateLimits?: DateLimits;
    selectedDate?: Date | null;
    onSelectDate?: (date: Date) => void;
    onGoBackOneMonth?: (date: Date) => void;
    onGoToNextMonth?: (date: Date) => void;
  };
  Element: HTMLDivElement;
}

export default class EnvoyOneUpCalendar extends Component<EnvoyOneUpCalendarSignature> {
  @tracked internalCurrentMonth: Date;

  constructor(...args: ConstructorParameters<typeof Component<EnvoyOneUpCalendarSignature>>) {
    super(...args);
    // Initialize to current month or selected date's month or today
    const initialDate = this.args.currentMonth || this.args.selectedDate || new Date();
    this.internalCurrentMonth = new Date(initialDate.getFullYear(), initialDate.getMonth(), 1);
  }

  get currentMonth(): Date {
    // Use controlled month if provided, otherwise use internal state
    return this.args.currentMonth ?? this.internalCurrentMonth;
  }

  get currentMonthFormatted(): string {
    return format(this.currentMonth, 'MMMM yyyy');
  }

  get selectedDates(): Date[] {
    // Default to today if no date is selected
    if (this.args.selectedDate) {
      return [this.args.selectedDate];
    }
    const today = new Date();
    today.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    return [today];
  }

  @action
  goBackOneMonth(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const newMonth = new Date(this.currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    this.internalCurrentMonth = newMonth;
    this.args.onGoBackOneMonth?.(newMonth);
  }

  @action
  goToNextMonth(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const newMonth = new Date(this.currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    this.internalCurrentMonth = newMonth;
    this.args.onGoToNextMonth?.(newMonth);
  }

  @action
  handleDateSelect(selectedDate: Date): void {
    this.args.onSelectDate?.(selectedDate);
  }

  <template>
    <div class="envoy-one-up-calendar flex flex-col gap-4" ...attributes>
      <div class="flex items-center justify-between">
        <button
          type="button"
          class="w-10 h-10 flex items-center justify-center rounded-md border border-carbon-20 bg-white hover:bg-carbon-5 transition-colors"
          {{on "click" this.goBackOneMonth}}
          data-test-calendar-prev-month
        >
          {{svgJar "chevron-grey-left" width="16" height="16"}}
        </button>
        <span class="text-base leading-none text-carbon-70 font-bold">
          {{this.currentMonthFormatted}}
        </span>
        <button
          type="button"
          class="w-10 h-10 flex items-center justify-center rounded-md border border-carbon-20 bg-white hover:bg-carbon-5 transition-colors"
          {{on "click" this.goToNextMonth}}
          data-test-calendar-next-month
        >
          {{svgJar "chevron-grey-right" width="16" height="16"}}
        </button>
      </div>

      <CalendarGrid
        @currentMonth={{this.currentMonth}}
        @selectedDates={{this.selectedDates}}
        @onSelectDate={{this.handleDateSelect}}
        @dateLimits={{@dateLimits}}
      />
    </div>
  </template>
}
